import React from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import { getRoiRequestViewSections } from "../../../features/roi-request-view/model/roi-request-view";
import {
  getDemoRoiRequestById,
  mapDemoStoredRequestToDetails,
} from "../../../features/roi-request-create/model/roi-request-create.storage";
import { RoiRequestCreateTriggerButton } from "../../../features/roi-request-create/ui/roi-request-create-flow";
import { downloadRoiRequestPdf } from "../../../features/roi-request-pdf/model/download-roi-request-pdf";
import { useRoiRequestDuplicate } from "../../../features/roi-request-duplicate/model/use-roi-request-duplicate";
import { RoiRequestDuplicateDialog } from "../../../features/roi-request-duplicate/ui/roi-request-duplicate-dialog";
import { RoiRequestDuplicateSuccessBanner } from "../../../features/roi-request-duplicate/ui/roi-request-duplicate-success-banner";
import { RoiRequestCreateSelectorTable } from "../../../features/roi-request-create/ui/roi-request-create-selector-table";
import { SectionSidebarNav } from "../../../shared/ui/section-sidebar-nav/section-sidebar-nav";
import { ROUTES } from "../../../system/router/routes";
import { MOCK_ROI_REQUESTS } from "./roi-requests.mock";
import * as Styled from "./roi-request-view.styled";

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

export const RoiRequestView = () => {
  const navigate = useNavigate();
  const { patientID, requestID } = useParams();
  const outletContext = useOutletContext<
    IPatientInfoOutletContext | undefined
  >();
  const setIsStretchContent = outletContext?.setIsStretchContent;
  const contentScrollerRef = React.useRef<HTMLDivElement | null>(null);
  const parsedRequestId = Number.parseInt(requestID ?? "", 10);

  const request = React.useMemo(
    () =>
      Number.isFinite(parsedRequestId)
        ? getDemoRoiRequestById({
            requestId: parsedRequestId,
            fallbackRequests: MOCK_ROI_REQUESTS,
          })
        : undefined,
    [parsedRequestId],
  );

  const requestDetails = React.useMemo(
    () => (request ? mapDemoStoredRequestToDetails(request) : null),
    [request],
  );

  const isRequestViewAllowed = request?.state === "COMPLETED";

  const viewSections = React.useMemo(
    () =>
      request && patientID
        ? getRoiRequestViewSections({
            patientId: patientID,
            request,
          })
        : [],
    [patientID, request],
  );

  const navigationSections = React.useMemo(
    () => [
      { id: "request-details", label: "Request Details" },
      ...viewSections.map((section) => ({
        id: section.id,
        label: section.label,
      })),
    ],
    [viewSections],
  );

  const [isPdfDownloading, setIsPdfDownloading] = React.useState(false);
  const {
    duplicateRequest,
    duplicateSuccessRequestId,
    openDuplicateDialog,
    closeDuplicateDialog,
    handleDuplicateSuccess,
    dismissDuplicateSuccess,
  } = useRoiRequestDuplicate();

  React.useEffect(() => {
    if (!setIsStretchContent) {
      return;
    }

    setIsStretchContent(true);

    return () => {
      setIsStretchContent(false);
    };
  }, [setIsStretchContent]);

  const handleBack = React.useCallback(() => {
    if (!patientID) {
      return;
    }

    navigate(
      ROUTES.PATIENT_DETAILS_ROI_REPORTS.replace(":patientID", patientID),
    );
  }, [navigate, patientID]);

  const handleDownloadPdf = React.useCallback(async () => {
    if (!request || !patientID || isPdfDownloading) {
      return;
    }

    setIsPdfDownloading(true);

    try {
      await downloadRoiRequestPdf({
        patientId: patientID,
        request,
      });
    } finally {
      setIsPdfDownloading(false);
    }
  }, [isPdfDownloading, patientID, request]);

  const handleEditDuplicatedRequest = React.useCallback(() => {
    if (!patientID || duplicateSuccessRequestId === null) {
      return;
    }

    navigate(
      ROUTES.PATIENT_DETAILS_ROI_REPORTS.replace(":patientID", patientID),
      {
        state: {
          editRequestId: duplicateSuccessRequestId,
        },
      },
    );
  }, [duplicateSuccessRequestId, navigate, patientID]);

  if (!patientID) {
    return null;
  }

  if (!request || !requestDetails) {
    return (
      <Styled.Container>
        <Styled.BackButton type="button" onClick={handleBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>Back</span>
        </Styled.BackButton>
        <Styled.Title>ROI Request Data</Styled.Title>
        <Styled.EmptyState>ROI request was not found.</Styled.EmptyState>
      </Styled.Container>
    );
  }

  if (!isRequestViewAllowed) {
    return (
      <Styled.Container>
        <Styled.BackButton type="button" onClick={handleBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>Back</span>
        </Styled.BackButton>
        <Styled.Title>ROI Request Data</Styled.Title>
        <Styled.EmptyState>
          Only requests in the Completed status can be viewed and downloaded.
        </Styled.EmptyState>
      </Styled.Container>
    );
  }

  const detailRows = [
    {
      label: "Status",
      value: (
        <Styled.StatusValue $status={requestDetails.roiStatus}>
          {requestDetails.roiStatus}
        </Styled.StatusValue>
      ),
    },
    { label: "MRN", value: requestDetails.mrn },
    { label: "LDA ID", value: requestDetails.ldaId },
    { label: "Request ID", value: requestDetails.requestId },
    { label: "Created By", value: requestDetails.createdBy },
    { label: "Date Created", value: requestDetails.dateCreated },
    { label: "Last Updated", value: requestDetails.lastUpdated },
    { label: "Completed By", value: requestDetails.completedBy },
    { label: "Date Completed", value: requestDetails.dateCompleted },
    { label: "Records", value: requestDetails.recordsCount },
  ];

  return (
    <Styled.Container>
      <Styled.BackButton type="button" onClick={handleBack}>
        <i className="pi pi-arrow-left" aria-hidden="true" />
        <span>Back</span>
      </Styled.BackButton>
      {duplicateSuccessRequestId !== null ? (
        <RoiRequestDuplicateSuccessBanner
          onEdit={handleEditDuplicatedRequest}
          onDismiss={dismissDuplicateSuccess}
        />
      ) : null}
      <Styled.Header>
        <Styled.Title>ROI Request Data</Styled.Title>
        {request ? (
          <Styled.HeaderActions>
            <Styled.SecondaryActionButton
              type="button"
              onClick={() => {
                if (request) {
                  openDuplicateDialog(request);
                }
              }}
            >
              <i className="pi pi-copy" aria-hidden="true" />
              <span>Duplicate</span>
            </Styled.SecondaryActionButton>
            <RoiRequestCreateTriggerButton
              type="button"
              onClick={handleDownloadPdf}
              disabled={isPdfDownloading}
            >
              <i className="pi pi-download" aria-hidden="true" />
              <span>
                {isPdfDownloading ? "Generating PDF..." : "Download ROI Report"}
              </span>
            </RoiRequestCreateTriggerButton>
          </Styled.HeaderActions>
        ) : null}
      </Styled.Header>
      <Styled.ContentLayout>
        <SectionSidebarNav
          ariaLabel="ROI request sections"
          sections={navigationSections}
          scrollContainerRef={contentScrollerRef}
        />
        <Styled.ContentScroller ref={contentScrollerRef}>
          <Styled.Section id="request-details">
            <Styled.SectionTitle>Request Details</Styled.SectionTitle>
            <Styled.RequestDetails>
              {detailRows.map((item) => (
                <Styled.DetailRow key={item.label}>
                  <Styled.DetailLabel>{item.label}</Styled.DetailLabel>
                  <Styled.DetailValue>{item.value}</Styled.DetailValue>
                </Styled.DetailRow>
              ))}
            </Styled.RequestDetails>
          </Styled.Section>
          {viewSections.map((section) => (
            <Styled.Section key={section.id} id={section.id}>
              <Styled.SectionTitle>{section.label}</Styled.SectionTitle>
              {section.type === "details" ? (
                <Styled.SectionDetails>
                  {section.groups.map((group) => (
                    <Styled.DetailsGroup
                      key={`${section.id}-${group.title ?? "details"}`}
                    >
                      {group.title ? (
                        <Styled.DetailsGroupTitle>
                          {group.title}
                        </Styled.DetailsGroupTitle>
                      ) : null}
                      <Styled.RequestDetails>
                        {group.rows.map((item) => (
                          <Styled.DetailRow
                            key={`${group.title ?? "details"}-${item.label}`}
                          >
                            <Styled.DetailLabel>
                              {item.label}
                            </Styled.DetailLabel>
                            <Styled.DetailValue>
                              {item.value}
                            </Styled.DetailValue>
                          </Styled.DetailRow>
                        ))}
                      </Styled.RequestDetails>
                    </Styled.DetailsGroup>
                  ))}
                </Styled.SectionDetails>
              ) : (
                <RoiRequestCreateSelectorTable
                  section={{
                    section_id: section.id,
                    label: section.label,
                    table: {
                      columns: section.columns,
                      rows: section.rows,
                    },
                  }}
                  searchTerm=""
                  selectedIds={[]}
                  isReadonly
                />
              )}
            </Styled.Section>
          ))}
        </Styled.ContentScroller>
      </Styled.ContentLayout>
      <RoiRequestDuplicateDialog
        request={duplicateRequest}
        fallbackRequests={MOCK_ROI_REQUESTS}
        visible={Boolean(duplicateRequest)}
        onClose={closeDuplicateDialog}
        onDuplicate={handleDuplicateSuccess}
      />
    </Styled.Container>
  );
};
